/**
 * ProbationTracking.js
 *
 * FEATURE 7 — Probation Tracking
 *
 * HR-only page listing every employee currently on probation, sorted by
 * days remaining (soonest first). Lets HR confirm an employee (probation
 * successfully completed) or extend their probation period with a new
 * end date.
 *
 * Calls (employees.routes.js):
 *   GET   /employees/probation
 *   PATCH /employees/:id/probation
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal,
    Form, Spinner, Table,
} from 'react-bootstrap';
import {
    UserCheck, Clock, RefreshCw, AlertTriangle, CheckCircle, Calendar,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const DaysBadge = ({ days, isOverdue }) => {
    if (isOverdue) return <Badge bg="danger">Overdue by {Math.abs(days)} day{Math.abs(days) !== 1 ? 's' : ''}</Badge>;
    if (days <= 7)  return <Badge bg="warning" text="dark">{days} day{days !== 1 ? 's' : ''} left</Badge>;
    if (days <= 30) return <Badge bg="info" text="dark">{days} days left</Badge>;
    return <Badge bg="light" text="dark">{days} days left</Badge>;
};

// ── Action Modal ─────────────────────────────────────────────────────────────
const ActionModal = ({ employee, onClose, onDone }) => {
    const [action, setAction] = useState('confirm');
    const defaultExtend = (() => {
        const d = employee.probation_end_date ? new Date(employee.probation_end_date) : new Date();
        d.setDate(d.getDate() + 30);
        return d.toISOString().slice(0, 10);
    })();
    const [newEndDate, setNewEndDate] = useState(defaultExtend);
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (action === 'extend' && !newEndDate) return toast.error('Select a new end date');
        setSaving(true);
        try {
            await axiosInstance.patch(`/employees/${employee.id}/probation`, {
                action,
                new_end_date: action === 'extend' ? newEndDate : undefined,
            });
            toast.success(action === 'confirm'
                ? `${employee.first_name}'s probation confirmed!`
                : `${employee.first_name}'s probation extended`);
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Action failed');
        } finally { setSaving(false); }
    };

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    Probation Decision — {employee.first_name} {employee.last_name}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="p-3 rounded-3 mb-3" style={{ background: '#f8fafc' }}>
                    <div className="d-flex justify-content-between small mb-1">
                        <span className="text-muted">Joined</span>
                        <span className="fw-medium">{fmtDate(employee.join_date)}</span>
                    </div>
                    <div className="d-flex justify-content-between small">
                        <span className="text-muted">Current probation end</span>
                        <span className="fw-medium">{fmtDate(employee.probation_end_date)}</span>
                    </div>
                </div>

                <div className="d-flex gap-2 mb-3">
                    {[['confirm', '✓ Confirm Employment'], ['extend', '↻ Extend Probation']].map(([val, label]) => (
                        <button key={val} type="button"
                            onClick={() => setAction(val)}
                            style={{
                                flex: 1, padding: '10px 8px', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
                                border: `1.5px solid ${action === val ? (val === 'confirm' ? '#059669' : '#d97706') : '#e2e8f0'}`,
                                background: action === val ? (val === 'confirm' ? '#f0fdf4' : '#fffbeb') : '#fff',
                                color: action === val ? (val === 'confirm' ? '#059669' : '#d97706') : '#94a3b8',
                            }}>
                            {label}
                        </button>
                    ))}
                </div>

                {action === 'extend' && (
                    <>
                        <Form.Label className="small fw-bold">New probation end date</Form.Label>
                        <Form.Control type="date" value={newEndDate}
                            onChange={e => setNewEndDate(e.target.value)}/>
                    </>
                )}
                {action === 'confirm' && (
                    <div className="text-muted small">
                        This marks {employee.first_name}'s employment as confirmed.
                        They will be notified automatically.
                    </div>
                )}

                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant={action === 'confirm' ? 'success' : 'warning'}
                        className="flex-grow-1 fw-bold" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : action === 'confirm' ? 'Confirm' : 'Extend'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// ════════════════════════════════════════════════════════════════════════════
const ProbationTracking = () => {
    const [employees, setEmployees] = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [acting,    setActing]    = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        axiosInstance.get('/employees/probation')
            .then(r => setEmployees(r.data.data || []))
            .catch(() => toast.error('Failed to load probation list'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const overdueCount = employees.filter(e => e.is_overdue).length;
    const endingSoonCount = employees.filter(e => !e.is_overdue && e.days_remaining <= 7).length;

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <UserCheck size={22} className="text-primary"/>Probation Tracking
                    </h4>
                    <p className="text-muted small mb-0">
                        {employees.length} employee{employees.length !== 1 ? 's' : ''} currently on probation
                    </p>
                </div>
                <Button size="sm" variant="outline-secondary" onClick={load}>
                    <RefreshCw size={13}/>
                </Button>
            </div>

            {employees.length > 0 && (
                <Row className="g-3 mb-4">
                    {[
                        { label: 'On Probation', value: employees.length, color: '#4f46e5' },
                        { label: 'Ending in \u22647 Days', value: endingSoonCount, color: '#d97706' },
                        { label: 'Overdue Decision', value: overdueCount, color: '#dc2626' },
                    ].map((s, i) => (
                        <Col key={i} xs={4}>
                            <Card className="border-0 shadow-sm p-3 text-center">
                                <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
                                <div className="text-muted small">{s.label}</div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
            ) : employees.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center">
                    <CheckCircle size={40} className="text-success mx-auto mb-3 opacity-70"/>
                    <h6 className="text-muted fw-bold">No one is currently on probation</h6>
                    <p className="text-muted small mb-0">
                        New hires will automatically appear here for 90 days from their join date.
                    </p>
                </Card>
            ) : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                <th className="ps-4">Employee</th>
                                <th>Department</th>
                                <th>Manager</th>
                                <th>Joined</th>
                                <th>Probation Ends</th>
                                <th>Status</th>
                                <th className="pe-4 text-end">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employees.map(emp => (
                                <tr key={emp.id} style={{ background: emp.is_overdue ? '#fef2f2' : undefined }}>
                                    <td className="ps-4">
                                        <div className="fw-medium">{emp.first_name} {emp.last_name}</div>
                                        <div className="text-muted" style={{ fontSize: 11 }}>{emp.employee_code}</div>
                                    </td>
                                    <td>{emp.department_name || '—'}</td>
                                    <td>{emp.manager_name || '—'}</td>
                                    <td><Calendar size={11} className="me-1 text-muted"/>{fmtDate(emp.join_date)}</td>
                                    <td className="fw-medium">{fmtDate(emp.probation_end_date)}</td>
                                    <td>
                                        <DaysBadge days={emp.days_remaining} isOverdue={emp.is_overdue}/>
                                    </td>
                                    <td className="pe-4 text-end">
                                        <Button size="sm" variant="outline-primary" onClick={() => setActing(emp)}>
                                            Review
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}

            {acting && (
                <ActionModal employee={acting} onClose={() => setActing(null)}
                    onDone={() => { setActing(null); load(); }}/>
            )}
        </div>
    );
};

export default ProbationTracking;
